// src/pages/LFundQuiz.js
import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import html2canvas from 'html2canvas';
import quizData from '../data/txfundquiz.json';

// ================= Static Quiz Title =================
const QUIZ_TITLE = "Electrical Transformers Fundamentals Quiz";

// ================= Styled Components (same as before) =================
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 60px 0 40px;
  background: linear-gradient(135deg, #f5f5f7 0%, #ffffff 50%, #f5f5f7 100%);
  border-radius: 24px;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    padding: 40px 0 30px;
    margin-bottom: 40px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  background: linear-gradient(45deg, #1d1d1f, #424245, #1d1d1f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 20px;
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 20px;
  color: #86868b;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.4;
  padding: 0 20px;
`;

const NameInputContainer = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin: 40px auto;
  flex-wrap: wrap;
  max-width: 500px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const Input = styled.input`
  padding: 12px 20px;
  font-size: 16px;
  border: 1px solid #d2d2d7;
  border-radius: 12px;
  width: 200px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #0071e3;
    box-shadow: 0 0 0 3px rgba(0, 113, 227, 0.1);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const Button = styled.button`
  background: #0071e3;
  color: white;
  border: none;
  padding: 12px 28px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 980px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 20px;

  &:hover {
    background: #005bb5;
    transform: scale(1.02);
  }

  &:disabled {
    background: #a1a1a6;
    cursor: not-allowed;
    transform: none;
  }
`;

const QuizContainer = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 28px;
  padding: 40px;
  margin: 40px 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 28px;
  }
`;

const QuestionText = styled.h3`
  font-size: 28px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 32px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 40px;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  background: #f5f5f7;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;

  &:hover {
    background: #e8e8ed;
    transform: translateX(4px);
  }

  input:checked + & {
    background: #e0f0ff;
    border-color: #0071e3;
  }
`;

const RadioInput = styled.input.attrs({ type: 'radio' })`
  width: 20px;
  height: 20px;
  accent-color: #0071e3;
`;

const OptionText = styled.span`
  font-size: 18px;
  color: #1d1d1f;
`;

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-top: 20px;
`;

const ProgressText = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: #6e6e73;
`;

const ResultCard = styled.div`
  background: linear-gradient(145deg, #ffffff, #f5f5f7);
  border-radius: 32px;
  padding: 40px;
  text-align: center;
  margin: 40px 0;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const ResultTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 24px;
  color: #1d1d1f;
`;

const ResultScore = styled.p`
  font-size: 48px;
  font-weight: 700;
  margin: 20px 0;
  color: #0071e3;
`;

const ResultStatus = styled.p`
  font-size: 28px;
  font-weight: 600;
  margin: 16px 0;
  color: ${props => (props.pass ? '#28a745' : '#dc3545')};
`;

const SaveButton = styled(Button)`
  background: #28a745;
  margin-top: 30px;

  &:hover {
    background: #218838;
  }
`;

const RestartButton = styled(Button)`
  background: #6c757d;
  margin-left: 20px;

  &:hover {
    background: #5a6268;
  }
`;

const ErrorMessage = styled.p`
  color: #dc3545;
  text-align: center;
  margin-top: 16px;
`;

// ================= Main Quiz Component =================
const LFundQuiz = () => {
  const [questions] = useState(() => {
    const shuffled = [...quizData].sort(() => Math.random() - 0.5);
    return shuffled;
  });
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState(new Array(quizData.length).fill(null));
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [error, setError] = useState('');

  const resultRef = useRef(null);

  const startQuiz = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter both first and last name.');
      return;
    }
    setError('');
    setQuizStarted(true);
  };

  const handleAnswer = (selectedLetter) => {
    const newAnswers = [...answers];
    newAnswers[currentIndex] = selectedLetter;
    setAnswers(newAnswers);
  };

  const goToNext = () => {
    if (answers[currentIndex] === null) {
      setError('Please select an answer before continuing.');
      return;
    }
    setError('');
    if (currentIndex + 1 < questions.length) {
      setCurrentIndex(currentIndex + 1);
    } else {
      let correctCount = 0;
      questions.forEach((q, idx) => {
        if (answers[idx] === q['Correct Choice']) {
          correctCount++;
        }
      });
      setScore(correctCount);
      setQuizCompleted(true);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setError('');
    }
  };

  const restartQuiz = () => {
    setQuizStarted(false);
    setQuizCompleted(false);
    setCurrentIndex(0);
    setAnswers(new Array(questions.length).fill(null));
    setScore(0);
    setFirstName('');
    setLastName('');
    setError('');
  };

  const saveAsImage = () => {
    if (resultRef.current) {
      html2canvas(resultRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
      }).then(canvas => {
        const link = document.createElement('a');
        link.download = `quiz-result-${firstName}-${lastName}.png`;
        link.href = canvas.toDataURL();
        link.click();
      }).catch(err => {
        console.error('Could not save image', err);
      });
    }
  };

  // Name entry screen
  if (!quizStarted) {
    return (
      <PageContainer>
        <HeroSection>
          <HeroTitle>📝 {QUIZ_TITLE}</HeroTitle>
          <HeroSubtitle>
            Test your knowledge on transmission & distribution systems.
          </HeroSubtitle>
          <NameInputContainer>
            <Input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </NameInputContainer>
          <Button onClick={startQuiz}>Start Quiz</Button>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </HeroSection>
      </PageContainer>
    );
  }

  // Quiz completed screen
  if (quizCompleted) {
    const total = questions.length;
    const percentage = ((score / total) * 100).toFixed(1);
    const isPass = parseFloat(percentage) > 75;

    return (
      <PageContainer>
        <HeroSection>
          <HeroTitle>🎉 {QUIZ_TITLE} – Completed</HeroTitle>
        </HeroSection>

        <ResultCard ref={resultRef}>
          <ResultTitle>Your Results</ResultTitle>
          <p style={{ fontSize: '24px', margin: '8px 0' }}>
            {firstName} {lastName}
          </p>
          <ResultScore>
            {score} / {total}
          </ResultScore>
          <p style={{ fontSize: '22px' }}>Percentage: {percentage}%</p>
          <ResultStatus pass={isPass}>
            {isPass ? '✅ PASS' : '❌ FAIL'}
          </ResultStatus>
        </ResultCard>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
          <SaveButton onClick={saveAsImage}>📸 Save as Image</SaveButton>
          <RestartButton onClick={restartQuiz}>🔄 Take Again</RestartButton>
        </div>
      </PageContainer>
    );
  }

  // Active quiz
  const currentQ = questions[currentIndex];
  const selected = answers[currentIndex];
  const progress = `${currentIndex + 1} of ${questions.length}`;

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>⚡ {QUIZ_TITLE}</HeroTitle>
        <HeroSubtitle>
          {firstName} {lastName} • Question {progress}
        </HeroSubtitle>
      </HeroSection>

      <QuizContainer>
        <QuestionText>{currentQ.Question}</QuestionText>
        <OptionsList>
          {['A', 'B', 'C', 'D'].map((letter) => (
            <OptionLabel key={letter}>
              <RadioInput
                name="question"
                value={letter}
                checked={selected === letter}
                onChange={() => handleAnswer(letter)}
              />
              <OptionText>
                <strong>{letter}.</strong> {currentQ[letter]}
              </OptionText>
            </OptionLabel>
          ))}
        </OptionsList>

        <Navigation>
          <Button onClick={goToPrevious} disabled={currentIndex === 0}>
            ← Previous
          </Button>
          <Button onClick={goToNext}>
            {currentIndex + 1 === questions.length ? 'Finish Quiz' : 'Next →'}
          </Button>
        </Navigation>
        <ProgressText>{progress} questions</ProgressText>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </QuizContainer>
    </PageContainer>
  );
};

export default LFundQuiz;